import { digObject, formatDate } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import api from '@functions/api'

import { handleError } from '@redux/utils'

import usePublicationBooking from '@hooks/usePublicationBooking'
import type { Contact } from '@hooks/usePublicationBooking'

import { PublicationBookingModel } from '@models/types'

import type { ModalCallbacks } from '../index'

const defaultState = {
  contacts: [],
  loading: false,
  message: '',
}

type LoadUnapprovedArtworkContactsParams = {
  publicationBooking: PublicationBookingModel,
  setState: (state: Partial<typeof defaultState>) => void,
}

const loadUnapprovedArtworkContacts = (params: LoadUnapprovedArtworkContactsParams) => {
  const { publicationBooking, setState } = params

  setState({ loading: true })

  api(`/publication_bookings/${publicationBooking.id}/unapproved_artwork_contacts.json`)
    .then(({ data }: { data: {contacts: Contact[] }}) => ({ success: true as const, data }))
    .catch((data) => {
      const errors = handleError(data)
      return { success: false as const, errors }
    })
    // @ts-expect-error TS2339
    .then(({ success, data }) => {
      setState({ loading: false })

      if (success){
        const { contacts } = data
        // @ts-expect-error TS2322
        setState({ contacts })
      }
    })
}

type DeselectContactParams = {
  contact: Contact,
  setState: (state: Partial<typeof defaultState>) => void,
  state: Partial<typeof defaultState>,
}

const deselectContact = (params: DeselectContactParams) => {
  const {
    contact: selectedContact,
    setState,
    state: { contacts },
  } = params

  // @ts-expect-error TS2339
  const filtered = contacts?.filter(contact => contact.user_id !== selectedContact.user_id)

  setState({
    contacts: filtered,
  })
}

type GetDefaultMessageParams = {
  publicationBookingPayload: ReturnType<typeof usePublicationBooking>,
  setState: (state: Partial<typeof defaultState>) => void,
}

const setDefaultMessage = (params: GetDefaultMessageParams) => {
  const { publicationBookingPayload, setState } = params
  const { publication, publicationBooking, publicationEdition } = publicationBookingPayload || {}

  if (!publicationBooking) return ''

  const artworkDate = digObject(publicationEdition, 'dates.artwork.date_time', '')

  // @ts-expect-error TS2339
  const editionDate = formatDate(publicationEdition?.publication_date, 'dd/MM/yyyy', "MMM d 'Edition'")
  const artworkDeadline = formatDate(artworkDate, 'dd/MM/yyyy T', 'h:mm a')

  const message = `Your booking for the ${publication.title} (${editionDate}) is missing an approved artwork. The artwork deadline is at ${artworkDeadline} (${publication.time_zone}), please submit your artwork before this time or your booking and associated costs could be forfeited.\n\nDo not reply to this number.\nFor assistance call (02) 9029 4025`

  return setState({ message })
}

type MessageBookingContactsParams = {
  callbacks: ModalCallbacks,
  publicationBooking: PublicationBookingModel,
}

const useMessageBookingContactsModal = (params: MessageBookingContactsParams) => {
  const { callbacks: { closeModal }, publicationBooking } = params

  const [state, setState] = useSetState(defaultState)
  const { contacts, loading, message } = state

  const saveEnabled = !!contacts.length && !!message

  const publicationBookingPayload = usePublicationBooking(publicationBooking)
  const {
    callbacks: {
      sendUnapprovedArtworkMessage: unapprovedArtworkMessageFn,
    },
  } = publicationBookingPayload

  const sendUnapprovedArtworkMessagePayload = {
    callbacks: {
      action: unapprovedArtworkMessageFn,
      afterAction: closeModal,
    },
    entityParams: {
      publicationBookingId: publicationBooking.id,
      contacts,
      message,
    },
    toastText: 'Message Sent',
  }

  return {
    callbacks: {
      deselectContact: (contact: Contact) => deselectContact({ contact, state, setState }),
      loadContacts: () => loadUnapprovedArtworkContacts({ publicationBooking, setState }),
      setDefaultMessage: () => setDefaultMessage({ publicationBookingPayload, setState }),
      setState,
    },
    contacts,
    loading,
    message,
    publicationBookingPayload,
    saveEnabled,
    sendUnapprovedArtworkMessagePayload,
  }
}

export default useMessageBookingContactsModal
